/**
 * Members asking to be received here.
 *
 * A transfer used to be something an administrator did to a member: pick a
 * destination, press the button, done. That is the right tool when the office
 * knows somebody has moved — but it is not how relocating actually works. The
 * member moves first, finds the local assembly, and turns up.
 *
 * So this is the other half: the queue of people who have asked, with the
 * assembly they are coming from, and a decision that belongs to the receiving
 * church. Approving moves them on the existing transfer history, so the roll
 * never shows one person at two assemblies.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Check, X, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import api, { getErrorMessage } from '@/lib/api';

export const TransferRequestsPanel = ({ churchId }) => {
  const qc = useQueryClient();
  const [declining, setDeclining] = useState('');
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['transfer-requests', churchId],
    queryFn: () => api.get(`/church/${churchId}/transfer-requests`).then((r) => r.data),
    enabled: !!churchId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['transfer-requests', churchId] });
    qc.invalidateQueries({ queryKey: ['church-members', churchId] });
  };

  const approve = useMutation({
    mutationFn: (id) => api.post(`/church/${churchId}/transfer-requests/${id}/approve`),
    onSuccess: () => { toast.success('Received — they are now on your roll'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not approve that request')),
  });

  const decline = useMutation({
    mutationFn: (id) => api.post(`/church/${churchId}/transfer-requests/${id}/decline`,
                                 { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('Request declined');
      setDeclining(''); setReason(''); refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not decline that request')),
  });

  if (isLoading) return null;

  const waiting = (data?.items || []).filter((r) => r.status === 'requested');
  const decided = (data?.items || []).filter((r) => r.status !== 'requested').slice(0, 5);
  if (!waiting.length && !decided.length) return null;

  return (
    <Card data-testid="transfer-requests-panel">
      <CardContent className="space-y-3 p-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
            <ArrowRightLeft className="h-4 w-4 text-slate-400" />
            Transfer requests
            {waiting.length ? (
              <Badge className="bg-amber-500 text-white hover:bg-amber-500">{waiting.length}</Badge>
            ) : null}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Existing FWM members asking to move their membership here. Approving moves
            them across with their history; their current assembly keeps them until you do.
          </p>
        </div>

        {waiting.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-200 p-3"
               data-testid={`transfer-request-${r.id}`}>
            <p className="text-sm font-semibold text-slate-900">{r.member_name || 'A member'}</p>
            <p className="text-sm text-slate-600">
              Currently at {r.from_church_name || 'another assembly'}
            </p>
            {r.reason ? <p className="mt-1 text-sm text-slate-500">“{r.reason}”</p> : null}
            {declining === r.id ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="Reason (optional, kept internal)"
                       className="max-w-xs" data-testid={`transfer-decline-reason-${r.id}`} />
                <Button size="sm" variant="destructive" disabled={decline.isPending}
                        onClick={() => decline.mutate(r.id)}
                        data-testid={`transfer-decline-confirm-${r.id}`}>
                  Confirm decline
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setDeclining(''); setReason(''); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" disabled={approve.isPending}
                        onClick={() => approve.mutate(r.id)}
                        data-testid={`transfer-approve-${r.id}`}>
                  {approve.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                     : <Check className="mr-1.5 h-4 w-4" />}
                  Receive them
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeclining(r.id)}
                        data-testid={`transfer-decline-${r.id}`}>
                  <X className="mr-1.5 h-4 w-4" />Decline
                </Button>
              </div>
            )}
          </div>
        ))}

        {decided.length ? (
          <div className="border-t border-slate-100 pt-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Clock className="h-3 w-3" />Recently decided
            </p>
            <ul className="mt-1.5 space-y-1">
              {decided.map((r) => (
                <li key={r.id} className="flex flex-wrap gap-2 text-sm text-slate-600">
                  <span>{r.member_name || 'A member'}</span>
                  <Badge variant="outline">{r.status}</Badge>
                  <span className="text-slate-400">from {r.from_church_name}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TransferRequestsPanel;
